import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import { exposuresService } from '~/services/exposures-service';
import { usePersonStore } from '~/stores/person';
import { useCompaniesStore } from '~/stores/person/companies';
import { useExposuresStore } from '~/stores/person/exposures';
import { ModalNames } from '~/types/modals';
import type { Company } from '~/types/person/companies';
import type { Exposure } from '~/types/person/details';
import { urlWithDrawerQuery } from '~/utils/drawer-history';
import { exposureId } from '~/utils/exposure-id';

export const useExposureSidebarStore = defineStore('exposure-sidebar', () => {
    const selectedExposure = ref<string>();
    const selectedCompanyId = ref<number>();
    // Bumped whenever an exposure fetch starts or is abandoned; only the newest may write.
    let exposureFetchGeneration = 0;
    const exposure = ref<Exposure>();
    // A drawer that cannot load its exposure has to say so: an empty one waits on a fetch that
    // already failed, and every skeleton in it spins for the rest of the session.
    const exposureError = ref<string>();
    // The company whose drawer is on its way while its exposure list is looked up, so the row that opens it shows that
    // whoever asked.
    const openingCompanyId = ref<number>();
    // Bumped whenever an open starts or is ended; only the newest may open the drawer.
    let openGeneration = 0;
    const personStore = usePersonStore();
    const companiesStore = useCompaniesStore();
    const exposuresStore = useExposuresStore();
    const { companies } = storeToRefs(companiesStore);

    // An exposure's drawer shows the company the exposure is at, and a company-only drawer the company it opened for.
    const company = computed(() => {
        if (exposure.value?.company_id) {
            return companies.value.find((c: Company) => c.id === exposure.value?.company_id);
        }
        if (selectedCompanyId.value) {
            return companies.value.find((c: Company) => c.id === selectedCompanyId.value);
        }
        return undefined;
    });

    /**
     * Disown any exposure fetch still in flight. Without this, closing a drawer mid-fetch and
     * opening a company card lets the old response land afterwards — and since `company` reads
     * `exposure.company_id` first, it would silently replace the company the member just picked.
     */
    function invalidateExposureFetch() {
        exposureFetchGeneration++;
    }

    async function fetchExposure(exposureId: string) {
        if (!exposureId || !personStore.person?.id) return;

        const generation = ++exposureFetchGeneration;

        try {
            exposure.value = undefined;
            exposureError.value = undefined;
            const fetched = await exposuresService.getExposure(personStore.person.id, exposureId);

            // The member moved on while this was in flight.
            if (generation !== exposureFetchGeneration) return;

            exposure.value = fetched;
        } catch (error) {
            if (generation !== exposureFetchGeneration) return;

            exposure.value = undefined;
            exposureError.value = (error instanceof Error && error.message) || 'Failed to load exposure details';
        }
    }

    /**
     * The open on its way opens nothing when its list arrives: a drawer opened since is the member's newer choice, and
     * a page they have started to leave is not the one the drawer belongs over.
     *
     * A page whose companies open their drawer (`openForCompany`) calls this from its leave guard. Vue Router runs leave
     * guards as a navigation off the page starts, before every global guard, and global guards in the order they were
     * registered: the ones registered at boot, ahead of any a store could add on first use, hold a navigation while
     * they read (the route middleware, and the person store's switch reading the next account). The list can arrive in
     * that wait, and a drawer opened then sits over the next page with its key on that page's address.
     */
    function invalidateOpening() {
        openGeneration++;
        openingCompanyId.value = undefined;
    }

    /**
     * The one way this drawer opens: it clears the other selection before setting its own, because
     * `company` reads the exposure's company first and a leftover selection would win over the
     * subject the member just picked.
     *
     * The history entry is what the drawer's own popstate handler closes on, so Back dismisses
     * the drawer rather than leaving the page under it.
     */
    function openExposure(exposureId: string) {
        invalidateOpening();
        selectedCompanyId.value = undefined;
        selectedExposure.value = exposureId;
        pushDrawerHistory(urlWithDrawerQuery('exposure', exposureId));
        useDynamicDialog(ModalNames.EXPOSURE_SIDEBAR);
    }

    /** Company-only mode: the company is in our database but we track no exposure from it. */
    function openCompany(companyId: number) {
        invalidateOpening();
        selectedExposure.value = undefined;
        selectedCompanyId.value = companyId;
        pushDrawerHistory(urlWithDrawerQuery('company', companyId));
        useDynamicDialog(ModalNames.EXPOSURE_SIDEBAR);
    }

    /**
     * A company's drawer, as a list of companies opens it: on the first exposure its list holds, and company-only when
     * it holds none. A failed lookup opens company-only too rather than a drawer with no subject at all; why the list
     * is empty is the exposures store's `companyExposuresError`.
     *
     * One open is on its way at a time, and the newest ask takes the drawer: a press of the row a link is opening, or a
     * second press of a row, opens one drawer, not a second over the first, on the one lookup the exposures store
     * shares between them. A drawer opened meanwhile, or the member starting to leave the page (its leave guard), ends
     * it (`invalidateOpening`).
     */
    async function openForCompany(companyId: number) {
        const generation = ++openGeneration;
        openingCompanyId.value = companyId;

        const companyExposures = await exposuresStore.loadForCompany(companyId);
        if (generation !== openGeneration) return;

        if (companyExposures.length) {
            openExposure(exposureId(companyExposures[0]));
        } else {
            openCompany(companyId);
        }
    }

    function pushDrawerHistory(url: string) {
        window.history.pushState({ modalOpen: true }, '', url);
    }

    // Picking an exposure reads it, then the company it is at, which the drawer's header shows.
    watch(selectedExposure, async (exposureId) => {
        if (exposureId) {
            await fetchExposure(exposureId);

            if (exposure.value?.company_id) {
                companiesStore.getCompanyById(exposure.value.company_id);
            }
        } else {
            invalidateExposureFetch();
            exposure.value = undefined;
            exposureError.value = undefined;
        }
    });

    // A company-only drawer reads its company, unless the store already holds it.
    watch(selectedCompanyId, async (companyId) => {
        if (companyId) {
            // A company was picked; no exposure response may claim this drawer any more.
            invalidateExposureFetch();
        }

        if (companyId && !exposure.value) {
            const existingCompany = companies.value.find((c: Company) => c.id === companyId);
            if (!existingCompany) {
                await companiesStore.fetchCompany(companyId);
            }
        }
    });

    return {
        selectedExposure,
        selectedCompanyId,
        exposure,
        exposureError,
        openExposure,
        openCompany,
        openForCompany,
        openingCompanyId,
        invalidateOpening,
        company,
        fetchExposure,
    };
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useExposureSidebarStore, import.meta.hot));
}
